import React, { useEffect, useState } from "react";
import { BarChart3 } from "lucide-react";
import ShareCardFrame, { Wordmark, scoreColor } from "@/components/share/ShareCardFrame";
import api from "@/lib/api";
import { TRAITS } from "@/lib/traits";
import { insightCaptions } from "@/components/share/CaptionBox";

const label = (key) => TRAITS.find((t) => t.key === key)?.label || key;

export default function InsightCard() {
  const [industry, setIndustry] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/insights/macro${industry ? `?industry=${encodeURIComponent(industry)}` : ""}`).then((r) => setData(r.data));
  }, [industry]);

  if (!data) return <p className="text-slate-400 font-mono text-sm">Compiling insights...</p>;
  const rf = data.red_flag;
  const month = new Date(data.generated_at).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const url = `${window.location.origin}/insights`;
  const flagText = rf
    ? `${data.industry === "All industries" ? "All sectors" : `${data.industry} sector`}: ${rf.tag} ${rf.change_pct !== null ? `is ${rf.change_pct >= 0 ? "up" : "down"} ${Math.abs(rf.change_pct)}% this month` : `tagged ${rf.count}× ${rf.window === "30d" ? "this month" : "to date"}`}`
    : "Not enough tagged reviews yet";

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="w-full max-w-[420px]">
        <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Industry sector</label>
        <select data-testid="insight-industry-select" value={industry} onChange={(e) => setIndustry(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm">
          <option value="">All industries</option>
          {data.industries.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
      </div>

      <ShareCardFrame fileName={`my360boss-insights-${(industry || "all").replace(/\s+/g, "-").toLowerCase()}`} shareText={`my360boss Macro Insights Report — ${data.industry}. ${flagText}. ${url}`} shareUrl={url} captions={insightCaptions(data, flagText, url)} testid="insight-card">
        <div className="px-6 pt-6 flex items-center justify-between">
          <Wordmark />
          <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: "#94A3B8" }}>{month}</span>
        </div>
        <div className="px-6 mt-4">
          <h2 className="leading-[1]" style={{ fontSize: "1.75rem", fontWeight: 900, letterSpacing: "-0.02em" }} data-testid="insight-card-title">my360boss Macro Insights Report</h2>
          <p className="text-sm mt-1.5" style={{ color: "#94A3B8" }}><BarChart3 size={13} className="inline mr-1" style={{ verticalAlign: -2 }} />{data.industry} · {data.review_count} anonymous reviews</p>
        </div>

        <ol className="px-6 mt-4 space-y-1.5" data-testid="insight-card-ranking">
          {data.trait_ranking.map((t, i) => (
            <li key={t.key} className="flex items-center gap-3">
              <span className="font-mono text-[11px] w-5 text-right" style={{ color: "#64748B" }}>{i + 1}</span>
              <span className="text-[13px] flex-1 truncate" style={{ color: i === data.trait_ranking.length - 1 ? "#FCA5A5" : "#E2E8F0", fontWeight: i === 0 || i === data.trait_ranking.length - 1 ? 700 : 500 }}>{label(t.key)}</span>
              <div className="w-24 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: "rgba(248,250,252,0.1)" }}>
                <div className="h-full" style={{ width: `${(t.avg / 5) * 100}%`, backgroundColor: scoreColor(t.avg) }} />
              </div>
              <span className="font-mono text-[12px] w-8 text-right" style={{ color: scoreColor(t.avg), fontWeight: 700 }}>{t.avg ? t.avg.toFixed(1) : "—"}</span>
            </li>
          ))}
        </ol>

        <div className="mx-6 mt-4 rounded-xl p-3.5" style={{ backgroundColor: "rgba(239,68,68,0.15)", border: "1px solid #EF4444" }} data-testid="insight-card-redflag">
          <p className="font-mono text-[10px] uppercase tracking-widest" style={{ color: "#FCA5A5" }}>Current National Workplace Red Flag</p>
          <p className="mt-1 text-[15px] leading-snug" style={{ fontWeight: 800 }}>{flagText}</p>
        </div>

        <div className="mt-auto px-6 pb-5">
          <div className="h-px w-full mb-3" style={{ backgroundColor: "rgba(248,250,252,0.25)" }} />
          <p className="text-[10px] leading-snug" style={{ color: "#CBD5E1" }}>Data compiled across all verified, anonymous worker entries. To track or protect your workplace, visit my360boss.com.</p>
        </div>
      </ShareCardFrame>
    </div>
  );
}
